import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiRequest, ApiError } from '../lib/api';
import { useApi } from '../lib/useApi';
import { COUNTIES_BY_USE } from '../lib/counties';
import { ExploreTabs } from '../components/ExploreTabs';
import { PostingGate } from '../components/PostingGate';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import {
  Alert, Button, Card, Field, LoadingBlock, Select, TextInput,
} from '../components/ui';
import '../styles/public.css';

/** Sell something. Free, no account (§46). */

interface Category { id: string; slug: string; name: string }

export function MarketplacePost() {
  const { pathname } = useLocation();
  const cats = useApi<{ categories: Category[] }>('/api/v1/public/marketplace/categories');

  const [form, setForm] = useState({
    title: '', categoryId: '', price: '', condition: '',
    county: '', town: '', sellerName: '', phone: '', whatsapp: '', email: '',
    description: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const body: Record<string, unknown> = {
        title: form.title.trim(),
        categoryId: form.categoryId,
        sellerName: form.sellerName.trim(),
        phone: form.phone.trim(),
        county: form.county.trim(),
      };
      // An empty price means "contact for price" and must be OMITTED, not sent
      // as 0 — zero is a real price meaning free.
      if (form.price.trim() !== '') body.price = Number(form.price);
      for (const k of ['town', 'whatsapp', 'email', 'description'] as const) {
        const v = form[k].trim();
        if (v) body[k] = v;
      }
      if (form.condition) body.condition = form.condition;

      const r = await apiRequest<{ item: { title: string } }>(
        '/api/v1/marketplace/post', { method: 'POST', body });
      setDone(r.item.title);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.status === 429
            ? 'That is several postings in a short time. Please try again later.'
            : err.message)
          : 'That could not be sent. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <>
        <PublicHeader />
        <main className="page public-page">
          <ExploreTabs path={pathname} />
          <Card title="Thank you">
            <p style={{ marginTop: 0 }}><strong>{done}</strong> has been sent for review.</p>
            <p>
              Somebody at NyumbaSuite checks every posting before it appears, so it will
              not be visible straight away. Listings run for 60 days.
            </p>
            <Link className="btn btn-primary" to="/marketplace">Back to the marketplace</Link>
          </Card>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        <div className="page-head">
          <div>
            <h1 className="page-title">Sell something</h1>
            <p className="page-sub">Free to post. Your listing runs for 60 days.</p>
          </div>
        </div>

        <PostingGate what="sell something">
        {cats.loading ? <LoadingBlock /> : (
          <form onSubmit={submit} style={{ maxWidth: 720 }}>
            {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}

            <Card title="What are you selling?">
              <div className="grid grid-2">
                <Field label="Title" htmlFor="p-title">
                  <TextInput id="p-title" required value={form.title} onChange={set('title')}
                    placeholder="Mahogany dining table, seats six" />
                </Field>
                <Field label="Category" htmlFor="p-cat">
                  <Select id="p-cat" required value={form.categoryId} onChange={set('categoryId')}>
                    <option value="">Choose a category…</option>
                    {(cats.data?.categories ?? []).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Price in KES (leave blank to be asked)" htmlFor="p-price">
                  <TextInput id="p-price" inputMode="numeric" value={form.price}
                    onChange={e => setForm(f => ({ ...f, price: e.target.value.replace(/[^0-9]/g, '') }))}
                    placeholder="18000" />
                </Field>
                <Field label="Condition (optional)" htmlFor="p-cond">
                  <Select id="p-cond" value={form.condition} onChange={set('condition')}>
                    <option value="">Not stated</option>
                    <option value="new">New</option>
                    <option value="like_new">Like new</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="for_parts">For parts</option>
                  </Select>
                </Field>
              </div>
              <Field label="Description (optional)" htmlFor="p-desc">
                <textarea id="p-desc" className="textarea" rows={4} maxLength={4000}
                  value={form.description} onChange={set('description')}
                  placeholder="Solid mahogany, one owner, small scratch on one leg. Collection from Kilimani." />
              </Field>
            </Card>

            <Card title="Where, and how to reach you">
              <p className="muted" style={{ marginTop: 0 }}>
                Your phone number is shown publicly so buyers can call you.
              </p>
              <div className="grid grid-2">
                <Field label="Your name" htmlFor="p-seller">
                  <TextInput id="p-seller" required value={form.sellerName}
                    onChange={set('sellerName')} placeholder="Grace Wanjiru" />
                </Field>
                <Field label="Phone" htmlFor="p-phone">
                  <TextInput id="p-phone" required value={form.phone} onChange={set('phone')}
                    placeholder="+254 7xx xxx xxx" />
                </Field>
                <Field label="County" htmlFor="p-county">
                  <Select id="p-county" required value={form.county} onChange={set('county')}>
                    <option value="">Choose a county…</option>
                    {COUNTIES_BY_USE.map(c => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label="Town or area (optional)" htmlFor="p-town">
                  <TextInput id="p-town" value={form.town} onChange={set('town')}
                    placeholder="Kilimani" />
                </Field>
                <Field label="WhatsApp (optional)" htmlFor="p-wa">
                  <TextInput id="p-wa" value={form.whatsapp} onChange={set('whatsapp')}
                    placeholder="+254 7xx xxx xxx" />
                </Field>
                <Field label="Email (optional)" htmlFor="p-email">
                  <TextInput id="p-email" type="email" value={form.email} onChange={set('email')}
                    placeholder="you@example.com" />
                </Field>
              </div>
            </Card>

            <div className="row" style={{ gap: 12, alignItems: 'center', marginTop: 16 }}>
              <Button type="submit" loading={busy}>Send for review</Button>
              <p className="muted" style={{ margin: 0 }}>
                We check every posting before it appears.
              </p>
            </div>
          </form>
        )}
        </PostingGate>
      </main>
      <SiteFooter />
    </>
  );
}
